import React, {useMemo, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {HeaderTitle} from '@/components/atoms';
import PeacockDataTable from '@/components/organisms/PeacockDataTable';
import {Header} from '@/utils/constants/header';
import './ProductsTable.scss';

const ProductsTable = (props) => {
  const {
    columns = [],
    records = [],
    isUploadTable = false,
    handleBulkAction = () => {},
    setPageIndex = () => {},
    setSearchArry = () => {},
    searchState = [],
    setSortingObj = () => {},
    pageIndex = 0,
    pageSize = 10,
    onRowsPerPageChange = () => {},
    productListData,
    handleResetAll = () => {},
    isLoading = false,
    isFetching = false,
    setRowSelection,
    fetchAllProducts,
    initialSortConfig
  } = props;

  // Tracks whether the user has armed the dataset-wide select-all model.
  // Set to true via formatSelectAllSummary (called by usePeacockSelection when
  // allMode becomes active), reset to false when selection drops to zero.
  const isSelectAllRef = useRef(false);

  const onRowSelect = useCallback(
    (selectedRecords) => {
      if (selectedRecords.length === 0) {
        isSelectAllRef.current = false;
      }
      setRowSelection(selectedRecords);
    },
    [setRowSelection]
  );

  // Called by usePeacockSelection only when allMode is active — safe to use
  // as a side-effectful flag setter (fires post-render, not during render).
  const formatSelectAllSummary = useCallback(
    ({effectiveCount, totalRecords: total}) => {
      isSelectAllRef.current = true;
      return `${effectiveCount} of ${total} selected`;
    },
    []
  );

  // rightSideActions: always visible in the toolbar row, right-aligned on the
  // same line as Reset All. requiresSelection: true disables them until at
  // least one row is checked (driven by selectionUi inside the hook).
  const rightSideActions = useMemo(() => {
    if (isUploadTable) return [];
    return [
      {
        id: 'include-btn',
        label: 'Include',
        variant: 'secondary',
        requiresSelection: true,
        onClick: async () => {
          await handleBulkAction(
            false,
            fetchAllProducts,
            searchState,
            isSelectAllRef.current
          );
        }
      },
      {
        id: 'exclude-btn',
        label: 'Exclude',
        variant: 'secondary',
        requiresSelection: true,
        onClick: async () => {
          await handleBulkAction(
            true,
            fetchAllProducts,
            searchState,
            isSelectAllRef.current
          );
        }
      }
    ];
  }, [isUploadTable, handleBulkAction, fetchAllProducts, searchState]);

  return (
    <div
      className='productsTable relative px-5'
      style={{backgroundColor: 'var(--color-background-layer-01)'}}
      data-testid='products-table'>
      <HeaderTitle
        headingLevel={Header.SectionTitle}
        title='Select Products to Include or Exclude'
        className='bg-transparent pl-6 pr-4 pt-6 text-lg font-bold text-[color:var(--color-text-primary)]'
      />

      <PeacockDataTable
        columns={columns}
        records={records || []}
        isLoading={isLoading}
        isFetching={isFetching}
        pageSize={pageSize}
        totalRecords={productListData?.totalRecords || 0}
        paginationConfig={{
          pageIndex,
          setPageIndex,
          onRowsPerPageChange
        }}
        sortingConfig={{
          handleSorting: setSortingObj,
          initialSortConfig
        }}
        filterConfig={{
          setSearchArry,
          onResetAll: handleResetAll,
          showResetAll: true,
          resetAllPosition: 'left'
        }}
        selectionConfig={{
          onRowSelect,
          enableSelectAllPages: true,
          formatSelectAllSummary
        }}
        toolbarConfig={{
          rightSideActions
        }}
      />
    </div>
  );
};

ProductsTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  records: PropTypes.arrayOf(PropTypes.object),
  isUploadTable: PropTypes.bool,
  handleBulkAction: PropTypes.func,
  setPageIndex: PropTypes.func,
  setSearchArry: PropTypes.func,
  searchState: PropTypes.array,
  setSortingObj: PropTypes.func,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  onRowsPerPageChange: PropTypes.func,
  productListData: PropTypes.shape({
    totalRecords: PropTypes.number
  }),
  handleResetAll: PropTypes.func,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  setRowSelection: PropTypes.func.isRequired,
  rowSelection: PropTypes.array,
  fetchAllProducts: PropTypes.func,
  initialSortConfig: PropTypes.object,
  initialSelectedCount: PropTypes.number
};

export default React.memo(ProductsTable);
